import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';
import useAxios from '../../hooks/useAxios';
import { MESSAGES_CONVERSATIONS_URL } from '../../utils/constant';
import { ConversationSummary, MessagePaginationInfo } from '../../interface/Message.interface';
import { useContext } from 'react';
import { LoginContext } from '../../contexts/LoginContext';

interface ConversationsResponse {
  success: boolean;
  conversations: ConversationSummary[];
  pagination: MessagePaginationInfo | null;
}

export function useConversations() {
  const { getWithAxios } = useAxios();
  const { currentUser } = useContext(LoginContext);

  return useQuery({
    queryKey: queryKeys.messages.conversations(),
    queryFn: async (): Promise<ConversationsResponse> => {
      const response = await getWithAxios(`${MESSAGES_CONVERSATIONS_URL}?page=1&limit=20`);
      return {
        success: response?.success ?? false,
        conversations: response?.conversations || [],
        pagination: response?.pagination || null,
      };
    },
    enabled: !!currentUser?.isLogged?.(),
    staleTime: 30 * 1000, // 30 seconds for conversations
  });
}

export function useInfiniteConversations() {
  const { getWithAxios } = useAxios();
  const { currentUser } = useContext(LoginContext);

  return useInfiniteQuery({
    queryKey: [...queryKeys.messages.conversations(), 'infinite'],
    queryFn: async ({ pageParam = 1 }): Promise<ConversationsResponse> => {
      const response = await getWithAxios(`${MESSAGES_CONVERSATIONS_URL}?page=${pageParam}&limit=20`);
      return {
        success: response?.success ?? false,
        conversations: response?.conversations || [],
        pagination: response?.pagination || null,
      };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination?.hasMore) {
        return (lastPage.pagination.page || 0) + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: !!currentUser?.isLogged?.(),
    staleTime: 30 * 1000,
  });
}
